import React, { useState } from 'react';
import { Input, Button, IconButton } from '@vkontakte/vkui';
import { Icon20ViewOutline, Icon20HideOutline } from '@vkontakte/icons';
import { requestPasswordReset, completePasswordReset } from '../../utils/authUtils';
import CodeStep from './CodeStep';
import logo from '../../assets/images/logo_512.png';

const ALLOWED_EMAIL_DOMAIN = '@rcfh.rosleshoz.gov.ru';

const linkStyle = {
  marginTop: '2px',
  background: 'none',
  border: 'none',
  color: '#6c757d',
  fontSize: '14px',
  cursor: 'pointer',
  textDecoration: 'underline',
};

/**
 * Сброс пароля: почта → код из письма → новый пароль. Оформлен как форма
 * входа (LoginPanel). После смены пароля человек сразу входит в систему, а
 * прежние входы на всех устройствах перестают действовать.
 */
const PasswordReset = ({ initialEmail = '', onLoginSuccess, onBack }) => {
  const [email, setEmail] = useState(initialEmail);
  const [challenge, setChallenge] = useState(null);
  const [resetToken, setResetToken] = useState('');
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const header = (title, subtitle) => (
    <div className="login-panel-header">
      <div className="login-panel-logo-container">
        <img src={logo} alt="Логотип" className="login-panel-logo" />
      </div>
      <h2 className="login-panel-title">{title}</h2>
      {subtitle && <p className="login-panel-subtitle">{subtitle}</p>}
    </div>
  );

  const handleEmail = async (event) => {
    event.preventDefault();
    setError('');
    if (!email.trim().toLowerCase().endsWith(ALLOWED_EMAIL_DOMAIN)) {
      setError(`Укажите корпоративную почту с доменом ${ALLOWED_EMAIL_DOMAIN}`);
      return;
    }
    setIsLoading(true);
    const result = await requestPasswordReset(email.trim());
    setIsLoading(false);
    if (result.success) {
      setChallenge(result.challenge);
    } else {
      setError(result.error);
    }
  };

  const handlePassword = async (event) => {
    event.preventDefault();
    setError('');
    if (password.length < 8) {
      setError('Пароль должен быть не короче 8 символов');
      return;
    }
    if (password !== repeat) {
      setError('Пароли не совпадают');
      return;
    }
    setIsLoading(true);
    const result = await completePasswordReset(resetToken, password);
    setIsLoading(false);
    if (result.success) {
      onLoginSuccess(result.data.user);
    } else if (result.status === 410) {
      setResetToken('');
      setChallenge(null);
      setError(result.error);
    } else {
      setError(result.error);
    }
  };

  if (resetToken) {
    const toggle = (
      <IconButton
        hoverMode="opacity"
        label={showPassword ? 'Скрыть пароль' : 'Показать пароль'}
        onClick={() => setShowPassword((v) => !v)}
        disabled={isLoading}
      >
        {showPassword ? <Icon20HideOutline /> : <Icon20ViewOutline />}
      </IconButton>
    );
    return (
      <>
        {header('Новый пароль', 'Не короче 8 символов, не только цифры. После смены пароля другие устройства выйдут из системы.')}
        <form onSubmit={handlePassword} className="login-panel-form">
          <div className="login-panel-field">
            <label htmlFor="new-password" className="login-panel-label">Новый пароль</label>
            <Input
              id="new-password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Введите новый пароль"
              disabled={isLoading}
              className="login-panel-input"
              after={toggle}
            />
          </div>
          <div className="login-panel-field">
            <label htmlFor="new-password-repeat" className="login-panel-label">Повторите пароль</label>
            <Input
              id="new-password-repeat"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              value={repeat}
              onChange={(e) => setRepeat(e.target.value)}
              placeholder="Ещё раз"
              disabled={isLoading}
              className="login-panel-input"
            />
          </div>
          {error && <div className="login-panel-error">{error}</div>}
          <Button
            type="submit"
            size="l"
            mode="outline"
            appearance="neutral"
            disabled={isLoading}
            className="login-panel-button"
            style={{ width: '100%' }}
          >
            {isLoading ? 'Сохранение...' : 'Сохранить и войти'}
          </Button>
        </form>
      </>
    );
  }

  if (challenge) {
    return (
      <CodeStep
        key={challenge.challenge_id}
        purpose="reset"
        email={email.trim()}
        challenge={challenge}
        onSuccess={(data) => {
          setError('');
          setResetToken(data.reset_token);
        }}
        onBack={() => setChallenge(null)}
      />
    );
  }

  return (
    <>
      {header('Восстановление пароля', 'Пришлём код на корпоративную почту, после него можно задать новый пароль.')}
      <form onSubmit={handleEmail} className="login-panel-form">
        <div className="login-panel-field">
          <label htmlFor="reset-email" className="login-panel-label">Почта</label>
          <Input
            id="reset-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={`name${ALLOWED_EMAIL_DOMAIN}`}
            disabled={isLoading}
            className="login-panel-input"
          />
        </div>
        {error && <div className="login-panel-error">{error}</div>}
        <Button
          type="submit"
          size="l"
          mode="outline"
          appearance="neutral"
          disabled={isLoading}
          className="login-panel-button"
          style={{ width: '100%' }}
        >
          {isLoading ? 'Отправка...' : 'Получить код'}
        </Button>
        <button type="button" onClick={onBack} className="login-panel-link" style={linkStyle}>
          Вернуться ко входу
        </button>
      </form>
    </>
  );
};

export default PasswordReset;
